import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { useActiveCrashes } from '@/features/shared/queries';
import { cancelCrash } from '@/api/endpoints/crashes';
import { queryKeys } from '@/api/queryKeys';
import { errorMessage } from '@/api/errors';
import type { CrashEvent } from '@/types/api';

// Must match settings.CRASH_CANCEL_WINDOW_SECONDS on the backend.
const CANCEL_WINDOW_SECONDS = 45;

function secondsLeft(crash: CrashEvent): number {
  const elapsed = (Date.now() - new Date(crash.timestamp).getTime()) / 1000;
  return Math.max(0, Math.ceil(CANCEL_WINDOW_SECONDS - elapsed));
}

function Row({ crash }: { crash: CrashEvent }) {
  const queryClient = useQueryClient();
  const [left, setLeft] = useState(() => secondsLeft(crash));

  useEffect(() => {
    const id = setInterval(() => setLeft(secondsLeft(crash)), 1000);
    return () => clearInterval(id);
  }, [crash]);

  const cancel = useMutation({
    mutationFn: () => cancelCrash(crash.id),
    onSuccess: () => {
      toast.success("Cancelled — no one else will be contacted.");
      queryClient.invalidateQueries({ queryKey: queryKeys.activeCrashes });
    },
    onError: (caught) => toast.error(errorMessage(caught)),
  });

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-2.5">
        <ShieldAlert className="mt-0.5 size-5 shrink-0 text-bad" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-ink">
            Impact detected on your vehicle
          </p>
          <p className="text-[13px] text-ink-secondary">
            {left > 0
              ? `Contacting your emergency contact${crash.severity === 'critical' ? ' and responders' : ''} in ${left}s unless you cancel.`
              : 'The alert window has closed. Help is being contacted.'}
          </p>
        </div>
      </div>
      <Button
        variant="danger"
        size="sm"
        onClick={() => cancel.mutate()}
        loading={cancel.isPending}
        disabled={left === 0}
        className="shrink-0"
      >
        I&apos;m OK — cancel
      </Button>
    </div>
  );
}

/**
 * Shown on every signed-in route while the owner has a crash inside its cancel
 * window. The one place a dazed driver can stop a false dispatch from the app.
 */
export function ActiveCrashBanner() {
  const { data } = useActiveCrashes();
  const pending = (data ?? []).filter((c) => c.status === 'pending');
  if (pending.length === 0) return null;

  return (
    <div className="border-b border-bad/30 bg-bad-soft">
      <div className="mx-auto max-w-7xl space-y-3 px-4 py-3 sm:px-6 lg:px-8">
        {pending.map((crash) => (
          <Row key={crash.id} crash={crash} />
        ))}
      </div>
    </div>
  );
}
